import type { TFunction } from 'i18next';
import { z } from 'zod';

/** UI hodnota osoby ve formuláři. `partyId` = Party.Id. */
const personOptionSchema = z.object({
  partyId: z.number(),
  fullName: z.string(),
});

export type PersonOption = z.infer<typeof personOptionSchema>;

/** Hodnoty přepínače typu dopravy; mapují se na číselný enum `SerieTransportType` (Flight=0, Bus=1, Combined=2). */
export const TRANSPORT_TYPE_OPTIONS = ['flight', 'bus', 'combined'] as const;

const transportTypeSchema = z.enum(TRANSPORT_TYPE_OPTIONS);

export type TransportTypeValue = z.infer<typeof transportTypeSchema>;

/**
 * Sdílená pole formuláře série (název, typ dopravy, poznámka, odpovědné osoby). Produktový formulář
 * je nadmnožinou – přidává jen povinný `productName`. Lokalizované hlášky se vkládají přímo do schématu.
 */
function sharedSerieShape(t: TFunction) {
  return {
    name: z.string().trim().min(1, t('serie.name.required')),
    transportType: transportTypeSchema,
    note: z.string(),
    responsiblePersons: z.object({
      productManager: personOptionSchema.nullable(),
      yieldManager: personOptionSchema.nullable(),
      tourLeader: personOptionSchema.nullable(),
      tourGuide: personOptionSchema.nullable(),
    }),
  };
}

/** Schéma formuláře série (productName se neověřuje – v tomto toku se nepoužívá). */
export function buildSerieFormSchema(t: TFunction) {
  return z.object({ productName: z.string(), ...sharedSerieShape(t) });
}

/** Schéma formuláře produktu (navíc povinný název produktu). */
export function buildProductFormSchema(t: TFunction) {
  return z.object({
    productName: z.string().trim().min(1, t('product.name.required')),
    ...sharedSerieShape(t),
  });
}

/**
 * Hodnoty formuláře série i produktu. Obě builder schémata mají strukturálně shodný výstup, takže
 * jeden typ pokrývá oba toky; `productName` zůstává v toku série prázdný a neodesílá se.
 */
export type SerieFormValues = z.infer<ReturnType<typeof buildSerieFormSchema>>;

/** Prázdné výchozí hodnoty pro režim zakládání. */
export function emptySerieFormValues(): SerieFormValues {
  return {
    productName: '',
    name: '',
    transportType: 'flight',
    note: '',
    responsiblePersons: {
      productManager: null,
      yieldManager: null,
      tourLeader: null,
      tourGuide: null,
    },
  };
}
